import React, { useState, useEffect } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import { Calendar as CalendarIcon, List, Plus, Filter } from 'lucide-react';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './AdminCalendar.css';
import adminCalendarService from '../../services/adminCalendarService';
import CreateAgencyEventModal from './CreateAgencyEventModal';
import AgencyEventDetailsModal from './AgencyEventDetailsModal';

const localizer = momentLocalizer(moment);

const AdminCalendar = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('month');
  const [date, setDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('calendar'); // 'calendar' or 'list'
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  
  // Filters
  const [filters, setFilters] = useState({
    event_type: '',
    agent_id: '',
    start_date: moment().startOf('month').format('YYYY-MM-DD'),
    end_date: moment().endOf('month').format('YYYY-MM-DD')
  });

  // Event type colors
  const eventColors = {
    'agency_event': '#dc2626', // Red
    'training': '#f97316', // Orange
    'meeting': '#6b7280', // Gray
    'deadline': '#eab308' // Yellow
  };

  useEffect(() => {
    fetchEvents();
  }, [date, view, filters]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      
      // Calculate date range based on current view
      let startDate, endDate;
      if (view === 'month') {
        startDate = moment(date).startOf('month').format('YYYY-MM-DD');
        endDate = moment(date).endOf('month').format('YYYY-MM-DD');
      } else if (view === 'week') {
        startDate = moment(date).startOf('week').format('YYYY-MM-DD');
        endDate = moment(date).endOf('week').format('YYYY-MM-DD');
      } else {
        startDate = moment(date).format('YYYY-MM-DD');
        endDate = moment(date).format('YYYY-MM-DD');
      }

      const filterParams = {
        start_date: startDate,
        end_date: endDate,
        ...(filters.event_type && { event_type: filters.event_type }),
        ...(filters.agent_id && { agent_id: filters.agent_id })
      };

      const data = await adminCalendarService.getEvents(filterParams);
      
      const formattedEvents = data.events.map(event => ({
        ...event,
        id: event.id,
        title: event.title,
        start: new Date(`${event.start_date}T${event.start_time || '00:00:00'}`),
        end: event.end_date 
          ? new Date(`${event.end_date}T${event.end_time || '23:59:59'}`)
          : new Date(`${event.start_date}T${event.end_time || '23:59:59'}`),
        allDay: event.all_day,
        resource: {
          ...event,
          color: event.color || eventColors[event.event_type] || '#6b7280'
        }
      }));
      
      setEvents(formattedEvents);
    } catch (error) {
      console.error('Error fetching calendar events:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
    setShowDetailsModal(true);
  };

  const handleEventCreated = () => {
    setShowCreateModal(false);
    fetchEvents();
  };

  const handleEventUpdated = () => {
    setShowDetailsModal(false);
    fetchEvents();
  };

  const handleEventDeleted = () => {
    setShowDetailsModal(false);
    fetchEvents();
  };

  const eventStyleGetter = (event) => {
    const backgroundColor = event.resource?.color || '#6b7280';
    const style = {
      backgroundColor,
      borderRadius: '4px',
      opacity: 0.9,
      color: 'white',
      border: '0px',
      display: 'block'
    };

    // Add special styling for mandatory events
    if (event.resource?.is_mandatory) {
      style.border = '2px solid #991b1b';
      style.fontWeight = 'bold';
    }

    return { style };
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="admin-calendar-container">
      <div className="calendar-header">
        <h2>Calendar & Events</h2>
        
        {/* Controls */}
        <div className="calendar-controls">
          <div className="calendar-actions">
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn-create-event"
            >
              <Plus className="w-4 h-4" />
              Create Event
            </button>
            
            <button
              onClick={() => setViewMode('calendar')}
              className={`btn-view-toggle ${viewMode === 'calendar' ? 'active' : ''}`}
            >
              <CalendarIcon className="w-4 h-4" />
              Calendar
            </button>
            
            <button
              onClick={() => setViewMode('list')}
              className={`btn-view-toggle ${viewMode === 'list' ? 'active' : ''}`}
            >
              <List className="w-4 h-4" />
              List
            </button>
          </div>

          {/* Filters */}
          <div className="calendar-filters">
            <div className="filter-group">
              <label>Event Type</label>
              <select
                value={filters.event_type}
                onChange={(e) => handleFilterChange('event_type', e.target.value)}
              >
                <option value="">All Types</option>
                <option value="agency_event">Agency Event</option>
                <option value="training">Training</option>
                <option value="meeting">Meeting</option>
                <option value="deadline">Deadline</option>
              </select>
            </div>
          </div>
        </div>

        {/* Event Legend */}
        <div className="event-legend">
          {Object.entries(eventColors).map(([type, color]) => (
            <div key={type} className="legend-item">
              <div className="legend-color" style={{ backgroundColor: color }}></div>
              <span className="capitalize">{type.replace('_', ' ')}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Calendar or List View */}
      {viewMode === 'calendar' ? (
        <div className="calendar-wrapper" style={{ height: '700px' }}>
          <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            view={view}
            onView={setView}
            date={date}
            onNavigate={setDate}
            onSelectEvent={handleSelectEvent}
            eventPropGetter={eventStyleGetter}
            popup
            showMultiDayTimes
            step={30}
            timeslots={2}
          />
        </div>
      ) : (
        <div className="events-list-view">
          {events.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">📅</div>
              <h3>No Events Found</h3>
              <p>Create your first agency event to get started</p>
            </div>
          ) : (
            <table className="events-table">
              <thead>
                <tr>
                  <th>Event</th>
                  <th>Type</th>
                  <th>Date</th>
                  <th>Agents</th>
                  <th>Responses</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {events.map((event) => (
                  <tr key={event.id} onClick={() => handleSelectEvent(event)}>
                    <td>
                      <div className="font-medium">{event.title}</div>
                      {event.resource?.is_mandatory && (
                        <span className="mandatory-badge">
                          ⚠️ Mandatory
                        </span>
                      )}
                    </td>
                    <td>
                      <span className={`event-type-badge event-type-${event.resource?.event_type}`}>
                        {event.resource?.event_type?.replace('_', ' ')}
                      </span>
                    </td>
                    <td>
                      {moment(event.start).format('MMM D, YYYY')}
                      {!event.allDay && ` at ${moment(event.start).format('h:mm A')}`}
                    </td>
                    <td>
                      {event.resource?.agent_count || 0} agents
                    </td>
                    <td>
                      <div className="response-summary">
                        <span className="response-badge response-accepted">
                          ✓ {event.resource?.accepted_count || 0}
                        </span>
                        <span className="response-badge response-pending">
                          ⏳ {event.resource?.pending_count || 0}
                        </span>
                      </div>
                    </td>
                    <td className="capitalize">{event.resource?.status || 'active'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Modals */}
      {showCreateModal && (
        <CreateAgencyEventModal
          onClose={() => setShowCreateModal(false)}
          onEventCreated={handleEventCreated}
        />
      )}

      {showDetailsModal && selectedEvent && (
        <AgencyEventDetailsModal
          event={selectedEvent}
          onClose={() => setShowDetailsModal(false)}
          onEventUpdated={handleEventUpdated}
          onEventDeleted={handleEventDeleted}
        />
      )}
    </div>
  );
};

export default AdminCalendar;
